<script setup lang="ts">
import { computed } from 'vue';
import { ArrowLeft, X, CornerDownLeft, User, Maximize2 } from 'lucide-vue-next';

const props = defineProps<{
  typedText: string;
}>();

const results = computed(() => {
  const text = props.typedText.toLowerCase();
  
  if (text.startsWith('arsenal live')) {
    return [
      { id: 1, type: 'team', text: 'Arsenal Liverpool', meta: '', icon: User },
      { id: 2, type: 'match', text: 'Arsenal Liverpool - Villa', meta: 'Fri 20:00', icon: 'pi pi-calendar' }
    ];
  }
  
  if (text.startsWith('arsenal')) {
    return [
      { id: 1, type: 'team', text: 'Arsenal', meta: '', icon: User },
      { id: 3, type: 'match', text: 'Arsenal - PSG', meta: 'Sat 30th 17:00', icon: 'pi pi-calendar' },
      { id: 4, type: 'match', text: 'Arsenal - Burnley', meta: 'Mon 18th 20:00', icon: 'pi pi-calendar' }
    ];
  }
  
  if (text.startsWith('arse')) {
    return [
      { id: 1, type: 'team', text: 'Arsenal', meta: '', icon: User },
      { id: 2, type: 'player', text: 'Arsen Zakharyan', meta: '', icon: User },
      { id: 3, type: 'match', text: 'Arsenal - PSG', meta: 'Sat 30th 17:00', icon: 'pi pi-calendar' },
      { id: 4, type: 'match', text: 'Arsenal - Burnley', meta: 'Mon 18th 20:00', icon: 'pi pi-calendar' }
    ];
  }
  
  return [];
});

const formatText = (fullText: string) => {
  const input = props.typedText;
  if (!fullText.toLowerCase().startsWith(input.toLowerCase())) {
    return { typed: '', suggested: fullText };
  }
  return {
    typed: fullText.slice(0, input.length),
    suggested: fullText.slice(input.length)
  };
};
</script>

<template>
  <div class="insight-modal-full">
    <div class="insight-header">
      <button class="i-back"><ArrowLeft :size="20" /></button>
      <div class="i-input-mock">
        <span>{{ typedText }}</span>
        <span class="blink-cursor">|</span>
      </div>
      <div class="i-actions">
        <X :size="20" class="text-gray-600" />
        <button class="i-enter-btn">
          <span>Enter</span>
          <CornerDownLeft :size="16" />
        </button>
      </div>
    </div>
    
    <div class="insight-results">
      <div v-for="(res, idx) in results" :key="res.id" class="i-res-row" :class="{ active: idx === 0 }">
        <div class="i-res-icon">
          <component v-if="typeof res.icon !== 'string'" :is="res.icon" :size="20" />
          <i v-else :class="res.icon"></i>
        </div>
        
        <div class="i-res-text">
          <div class="i-res-title-wrap">
            <span class="i-typed">{{ formatText(res.text).typed }}</span>
            <span class="i-suggested">{{ formatText(res.text).suggested }}</span>
          </div>
          <span v-if="res.meta" class="i-res-meta">{{ res.meta }}</span>
        </div>
        
        <div class="i-res-actions">
          <Maximize2 v-if="res.type === 'team' || res.type === 'player'" :size="16" class="i-icon-dim" />
          <div class="i-arrow-pair">
            <i class="pi pi-arrow-up-left i-icon-dim"></i>
            <CornerDownLeft :size="16" class="i-icon-dim" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.insight-modal-full { position: absolute; inset: 0; background: #111113; z-index: 150; padding-top: 40px; }
.insight-header { display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-bottom: 1px solid rgba(255,255,255,0.05); }
.i-back { background: transparent; border: none; color: #fff; }
.i-input-mock { flex: 1; color: #fff; font-size: 13px; font-weight: 400; display: flex; align-items: center; }
.blink-cursor { color: #3b82f6; font-weight: 100; margin-left: 2px; animation: blink 1s infinite; }
@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
.i-actions { display: flex; align-items: center; gap: 10px; }
.i-enter-btn { background: #e21b0d; border: none; border-radius: 6px; padding: 5px 10px; color: #fff; display: flex; align-items: center; gap: 6px; font-weight: 700; font-size: 11px; }

.insight-results { padding: 4px 0; }
.i-res-row { display: flex; align-items: center; gap: 14px; padding: 12px 16px; border-bottom: 1px solid rgba(255,255,255,0.02); }
.i-res-row.active { background: rgba(255,255,255,0.03); }
.i-res-icon { color: #777; width: 20px; display: flex; justify-content: center; }
.i-res-text { flex: 1; display: flex; flex-direction: column; }

.i-res-title-wrap { font-size: 13px; color: #fff; display: flex; white-space: pre; font-style: italic; }
.i-typed { font-weight: 400; opacity: 0.8; }
.i-suggested { font-weight: 800; }

.i-res-meta { font-size: 11px; color: #555; margin-top: 1px; }
.i-res-actions { display: flex; align-items: center; gap: 20px; color: #444; }
.i-arrow-pair { display: flex; gap: 20px; }
.i-icon-dim { opacity: 0.6; }
</style>
